import React, { useMemo, useState } from 'react';
import { Badge, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PaidIcon from '@mui/icons-material/Paid';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CommentIcon from '@mui/icons-material/Comment';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { marketingPersonApi, DetailsOrganizerRow } from '../../../modules/services/marketingPersonService';
import { useAppSelector } from '../../../modules/store/hooks';
import { downloadMarketingPersonsExcel } from '../../../common/downloadMarketingPersonsExcel';
import useStyles from './useStyles';
import translate from '../../../i18n/translate';
import AddPayoutWizard from '../AddPayoutWizard';
import AddAssignmentModal from '../AddAssignmentModal';
import OrganizerInfoTooltip from './OrganizerInfoTooltip';
import EventInfoTooltip from './EventInfoTooltip';
import OrganizerCommentsModal from '../OrganizerCommentsModal';

type PaidFilter = 'all' | 'paid' | 'unpaid';

const MarketingPersonDetailsScreen: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const marketingPersonId = Number(id);

  const prevMonthStart = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
  const prevMonthEnd = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
  const [startPeriod, setStartPeriod] = useState<string>(prevMonthStart);
  const [endPeriod, setEndPeriod] = useState<string>(prevMonthEnd);
  const [organizerFilter, setOrganizerFilter] = useState<PaidFilter>('all');
  const [eventFilter, setEventFilter] = useState<PaidFilter>('all');
  const [search, setSearch] = useState<string>('');

  const filter = useMemo(() => ({
    marketingPersonId,
    startPeriod: startPeriod || undefined,
    endPeriod: endPeriod || undefined,
    organizerFilter,
    eventFilter,
    search: search || undefined,
  }), [marketingPersonId, startPeriod, endPeriod, organizerFilter, eventFilter, search]);

  const { data, isFetching, refetch } = marketingPersonApi.useGetMarketingPersonDetailsQuery(filter, {
    skip: !marketingPersonId,
  });

  const [payoutWizardOpen, setPayoutWizardOpen] = useState(false);
  const [addAssignmentOpen, setAddAssignmentOpen] = useState(false);
  const { data: payoutsForPerson, refetch: refetchPayouts } = marketingPersonApi.useListPayoutsQuery({ marketingPersonId }, { skip: !marketingPersonId });

  const language = useAppSelector((state) => state.auth.language);
  const lang = language?.split('-')?.[0] || 'en';
  const [commentsRow, setCommentsRow] = useState<DetailsOrganizerRow | null>(null);
  const [unbindRow, setUnbindRow] = useState<DetailsOrganizerRow | null>(null);
  const [unbindError, setUnbindError] = useState('');
  const [removeAssignment, { isLoading: isUnbinding }] = marketingPersonApi.useRemoveOrganizerAssignmentMutation();

  const handleUnbind = async () => {
    if (!unbindRow) return;
    setUnbindError('');
    try {
      await removeAssignment({ assignmentId: unbindRow.assignmentId, language: lang }).unwrap();
      setUnbindRow(null);
      refetch();
    } catch (e: any) {
      setUnbindError(e?.data?.message || e?.message || 'Failed to unbind organizer');
    }
  };

  const organizerColumns = [
    { field: 'organizerId', headerName: 'ID', flex: 0.4, minWidth: 60 },
    {
      field: 'organizerName',
      headerName: translate('marketingPersonsGridOrganizerName'),
      flex: 1.5, minWidth: 140,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', justifyContent: 'center' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.row.organizerName}
          </span>
          <OrganizerInfoTooltip organizerId={params.row.organizerUserId} />
        </div>
      ),
    },
    { field: 'organizerPhone', headerName: translate('marketingPersonsGridPhone'), flex: 1, minWidth: 120 },
    { field: 'eventsCount', headerName: translate('marketingPersonsEventsCount'), flex: 0.6, minWidth: 80 },
    { field: 'ticketsSold', headerName: translate('marketingPersonsTotalTickets'), flex: 0.6, minWidth: 80 },
    {
      field: 'totalAmount',
      headerName: translate('marketingPersonsTotalAmount'),
      flex: 0.9, minWidth: 100,
      renderCell: (params: any) =><span>₪{(params.row.totalAmount || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalFee',
      headerName: translate('marketingPersonsGridTotalFee'),
      flex: 0.9, minWidth: 100,
      renderCell: (params: any) =><Typography variant="body2" color="warning.main" fontWeight="bold">₪{(params.row.totalFee || 0).toLocaleString()}</Typography>,
    },
    {
      field: 'totalCommission',
      headerName: translate('marketingPersonsGridTotalCommission'),
      flex: 0.9, minWidth: 105,
      renderCell: (params: any) =><Typography variant="body2" color="success.main" fontWeight="bold">₪{(params.row.totalCommission || 0).toLocaleString()}</Typography>,
    },
    {
      field: 'commissionValue',
      headerName: translate('marketingPersonsGridCommissionValue'),
      flex: 0.7, minWidth: 90,
      renderCell: (params: any) =>(
        <Typography variant="body2" fontWeight="bold">
          {params.row.commissionValue}{params.row.commissionType === 'percent' ? '%' : ' ₪'}
        </Typography>
      ),
    },
    {
      field: 'referralCommission',
      headerName: translate('marketingPersonsReferralCommission'),
      flex: 0.8, minWidth: 100,
      renderCell: (params: any) => (
        <Typography variant="body2" color="warning.main" fontWeight="bold">
          ₪{(params.row.referralCommission || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'isReferralPaid',
      headerName: translate('marketingPersonsGridReferralPaid'),
      flex: 1.1, minWidth: 150,
      renderCell: (params: any) =>(
        <Chip
          icon={<PaidIcon />}
          label={params.row.isReferralPaid ? translate('marketingPersonsReferralPaid') : translate('marketingPersonsReferralUnpaid')}
          color={params.row.isReferralPaid ? 'success' : 'error'}
          size="small"
          variant={params.row.isReferralPaid ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: translate('marketingPersonsGridActions'),
      flex: 0.7, minWidth: 100,
      sortable: false,
      renderCell: (params: any) => (
        <>
          <Tooltip title={translate('marketingPersonsComments')}>
            <IconButton size="small" color="warning" onClick={() => setCommentsRow(params.row)}>
              <Badge badgeContent={params.row.commentsCount} color="warning" max={99}>
                <CommentIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title={translate('marketingPersonsUnbind')}>
            <IconButton size="small" color="error" onClick={() => { setUnbindError(''); setUnbindRow(params.row); }}>
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const eventColumns = [
    { field: 'eventId', headerName: 'ID', flex: 0.4, minWidth: 60 },
    {
      field: 'eventName',
      headerName: translate('marketingPersonsGridEventName'),
      flex: 1.6, minWidth: 160,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', justifyContent: 'center' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.row.eventName}
          </span>
          <EventInfoTooltip eventId={params.row.eventId} />
        </div>
      ),
    },
    {
      field: 'organizerName',
      headerName: translate('marketingPersonsGridOrganizerName'),
      flex: 1.2, minWidth: 130,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', justifyContent: 'center' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.row.organizerName}
          </span>
          <OrganizerInfoTooltip organizerId={params.row.organizerUserId} />
        </div>
      ),
    },
    {
      field: 'eventEndDate',
      headerName: translate('marketingPersonsGridEventDate'),
      flex: 0.9, minWidth: 100,
      renderCell: (params: any) =>(
        <span>{params.row.eventEndDate ? new Date(params.row.eventEndDate).toLocaleDateString() : '-'}</span>
      ),
    },
    { field: 'ticketsSold', headerName: translate('marketingPersonsTotalTickets'), flex: 0.6, minWidth: 80 },
    {
      field: 'totalAmount',
      headerName: translate('marketingPersonsTotalAmount'),
      flex: 0.9, minWidth: 100,
      renderCell: (params: any) =><span>₪{(params.row.totalAmount || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalFee',
      headerName: translate('marketingPersonsGridTotalFee'),
      flex: 0.9, minWidth: 100,
      renderCell: (params: any) =><Typography variant="body2" color="warning.main" fontWeight="bold">₪{(params.row.totalFee || 0).toLocaleString()}</Typography>,
    },
    {
      field: 'totalCommission',
      headerName: translate('marketingPersonsGridTotalCommission'),
      flex: 0.9, minWidth: 105,
      renderCell: (params: any) =><Typography variant="body2" color="success.main" fontWeight="bold">₪{(params.row.totalCommission || 0).toLocaleString()}</Typography>,
    },
    {
      field: 'isPaid',
      headerName: translate('marketingPersonsGridPaid'),
      flex: 0.9, minWidth: 110,
      renderCell: (params: any) =>(
        <Chip
          icon={<PaidIcon />}
          label={params.row.isPaid ? translate('marketingPersonsPaid') : translate('marketingPersonsUnpaid')}
          color={params.row.isPaid ? 'success' : 'error'}
          size="small"
          variant={params.row.isPaid ? 'filled' : 'outlined'}
        />
      ),
    },
  ];

  const payoutColumns = [
    { field: 'id', headerName: 'ID', flex: 0.4, minWidth: 60 },
    {
      field: 'createDate',
      headerName: translate('marketingPersonsPaymentCreatedAt'),
      flex: 0.7, minWidth: 110,
      renderCell: (p: any) => <span>{p.row.createDate ? new Date(p.row.createDate).toLocaleDateString() : '-'}</span>,
    },
    {
      field: 'period',
      headerName: 'Period',
      flex: 1.1, minWidth: 170,
      renderCell: (p: any) => (
        <span>
          {p.row.dateFrom ? new Date(p.row.dateFrom).toLocaleDateString() : '-'}
          {' → '}
          {p.row.dateTo ? new Date(p.row.dateTo).toLocaleDateString() : '-'}
        </span>
      ),
    },
    { field: 'eventItemsCount', headerName: 'Events', flex: 0.4, minWidth: 70 },
    { field: 'referralItemsCount', headerName: 'Referrals', flex: 0.5, minWidth: 80 },
    {
      field: 'totalEventCommission',
      headerName: 'Event Commission',
      flex: 0.8, minWidth: 110,
      renderCell: (p: any) => <span>₪{Number(p.row.totalEventCommission || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalReferralCommission',
      headerName: 'Referral',
      flex: 0.7, minWidth: 100,
      renderCell: (p: any) => <span>₪{Number(p.row.totalReferralCommission || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalAmount',
      headerName: 'Total',
      flex: 0.7, minWidth: 100,
      renderCell: (p: any) => <Typography variant="body2" color="success.main" fontWeight="bold">₪{Number(p.row.totalAmount || 0).toLocaleString()}</Typography>,
    },
    {
      field: 'actions',
      headerName: translate('marketingPersonsGridActions'),
      flex: 0.5, minWidth: 80,
      sortable: false,
      renderCell: (p: any) => (
        <IconButton size="small" color="warning" onClick={() => navigate(`/marketing-persons/payouts/${p.row.id}`)}>
          <span style={{ fontSize: 18 }}>↗</span>
        </IconButton>
      ),
    },
  ];

  return (
    <div className={classes.Wrapper}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className={classes.Header}>
          <Link color="warning.main" className={classes.ToMain} ml="10px" onClick={() => navigate('/marketing-persons/all')}>
            {translate('marketingPersonsBackToList')}
          </Link>
          <Typography fontSize={20} color="warning.main" fontWeight="bold">
            {translate('marketingPersonsDetailsTitle')}
          </Typography>
        </div>

        <div className={classes.FilterRow}>
          <TextField
            label={translate('marketingPersonsSearch')}
            size="small"
            color="warning"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <DatePicker
            label={translate('marketingPersonsPeriodStart')}
            value={startPeriod ? dayjs(startPeriod) : null}
            onChange={(value: Dayjs | null) => setStartPeriod(value ? value.format('YYYY-MM-DD') : '')}
            renderInput={(params) => <TextField {...params} size="small" color="warning" />}
          />
          <DatePicker
            label={translate('marketingPersonsPeriodEnd')}
            value={endPeriod ? dayjs(endPeriod) : null}
            onChange={(value: Dayjs | null) => setEndPeriod(value ? value.format('YYYY-MM-DD') : '')}
            renderInput={(params) => <TextField {...params} size="small" color="warning" />}
          />
          <TextField
            select
            size="small"
            color="warning"
            label={translate('marketingPersonsOrganizerFilter')}
            value={organizerFilter}
            onChange={(e) => setOrganizerFilter(e.target.value as PaidFilter)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">{translate('marketingPersonsAll')}</MenuItem>
            <MenuItem value="paid">{translate('marketingPersonsReferralPaid')}</MenuItem>
            <MenuItem value="unpaid">{translate('marketingPersonsReferralUnpaid')}</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            color="warning"
            label={translate('marketingPersonsEventFilter')}
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value as PaidFilter)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">{translate('marketingPersonsAll')}</MenuItem>
            <MenuItem value="paid">{translate('marketingPersonsPaid')}</MenuItem>
            <MenuItem value="unpaid">{translate('marketingPersonsUnpaid')}</MenuItem>
          </TextField>
          <Button
            color="warning"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() =>
              downloadMarketingPersonsExcel({
                marketingPersonId,
                startPeriod: startPeriod || undefined,
                endPeriod: endPeriod || undefined,
              })
            }
          >
            {translate('marketingPersonsDownloadAllExcel')}
          </Button>
          {(search || startPeriod || endPeriod || organizerFilter !== 'all' || eventFilter !== 'all') && (
            <Tooltip title={translate('marketingPersonsClearFilters')}>
              <IconButton size="small" color="warning" onClick={() => {
                setSearch('');
                setStartPeriod('');
                setEndPeriod('');
                setOrganizerFilter('all');
                setEventFilter('all');
              }}>
                <FilterAltOffIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div className={classes.SummarySection}>
          <Typography variant="h6" color="warning.main">
            {translate('marketingPersonsReportSummary')}
          </Typography>
          <div className={classes.SummaryStats}>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalTickets')}</Typography>
              <Typography variant="h6" color="warning.main">{data?.summary.ticketsSold ?? 0}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalAmount')}</Typography>
              <Typography variant="h6" color="warning.main">₪{(data?.summary.totalAmount ?? 0).toLocaleString()}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalFee')}</Typography>
              <Typography variant="h6" color="warning.main">₪{(data?.summary.totalFee ?? 0).toLocaleString()}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsOrganizersCount')}</Typography>
              <Typography variant="h6" color="warning.main">{data?.summary.organizersCount ?? 0}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsEventsCount')}</Typography>
              <Typography variant="h6" color="warning.main">{data?.summary.eventsCount ?? 0}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalCommission')}</Typography>
              <Typography variant="h6" color="success.main">₪{(data?.summary.totalCommission ?? 0).toLocaleString()}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsPaidCommission')}</Typography>
              <Typography variant="h6" color="success.main">₪{(data?.summary.paidCommission ?? 0).toLocaleString()}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsUnpaidCommission')}</Typography>
              <Typography variant="h6" color="error.main">₪{(data?.summary.unpaidCommission ?? 0).toLocaleString()}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsTotalReferralCommission')}</Typography>
              <Typography variant="h5" color="success.main">₪{(data?.summary.totalReferralCommission ?? 0).toLocaleString()}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsPaidReferralCommission')}</Typography>
              <Typography variant="h6" color="success.main">₪{(data?.summary.paidReferralCommission ?? 0).toLocaleString()}</Typography>
            </div>
            <div className={classes.StatCard}>
              <Typography variant="body2" color="textSecondary">{translate('marketingPersonsUnpaidReferralCommission')}</Typography>
              <Typography variant="h6" color="error.main">₪{(data?.summary.unpaidReferralCommission ?? 0).toLocaleString()}</Typography>
            </div>
          </div>
        </div>

        <div className={classes.SectionHeader}>
          <Typography variant="h6" color="warning.main">{translate('marketingPersonsDetailsOrganizersSection')}</Typography>
          <Button
            size="small"
            color="warning"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddAssignmentOpen(true)}
          >
            {translate('marketingPersonsAddAssignment')}
          </Button>
        </div>

        {isFetching ? (
          <CircularProgress color="warning" />
        ) : (
          <DataGrid
            sx={{
              backgroundColor: '#ede9e4',
              borderRadius: '0px',
              border: 'none',
              marginBottom: '24px',
              '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
              '& .MuiDataGrid-main': { overflow: 'visible' },
              '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', position: 'sticky', zIndex: 2, borderRadius: '0px', border: '0px' },
              '.MuiDataGrid-columnHeaderTitleContainer': { color: '#fff', fontSize: '15px', padding: '10px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
              '.MuiDataGrid-cell': { border: '1px solid #fc7f03', padding: '5px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
              '.MuiDataGrid-footerContainer': { border: '1px solid #fc7f03' },
              '.MuiDataGrid-footerContainer p': { margin: '0px' },
              '.MuiDataGrid-columnHeaderTitle': { overflow: 'visible', lineHeight: '1.43rem', whiteSpace: 'normal' },
            }}
            rows={(data?.organizers || []).map((r) => ({ ...r, id: r.assignmentId }))}
            columns={organizerColumns as unknown as GridColDef[]}
            getRowHeight={() => 'auto'}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            density="comfortable"
          />
        )}

        <div className={classes.SectionHeader}>
          <Typography variant="h6" color="warning.main">{translate('marketingPersonsDetailsEventsSection')}</Typography>
        </div>

        {isFetching ? (
          <CircularProgress color="warning" />
        ) : (
          <DataGrid
            sx={{
              backgroundColor: '#ede9e4',
              borderRadius: '0px',
              border: 'none',
              marginBottom: '24px',
              '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
              '& .MuiDataGrid-main': { overflow: 'visible' },
              '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', position: 'sticky', zIndex: 2, borderRadius: '0px', border: '0px' },
              '.MuiDataGrid-columnHeaderTitleContainer': { color: '#fff', fontSize: '15px', padding: '10px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
              '.MuiDataGrid-cell': { border: '1px solid #fc7f03', padding: '5px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
              '.MuiDataGrid-footerContainer': { border: '1px solid #fc7f03' },
              '.MuiDataGrid-footerContainer p': { margin: '0px' },
              '.MuiDataGrid-columnHeaderTitle': { overflow: 'visible', lineHeight: '1.43rem', whiteSpace: 'normal' },
            }}
            rows={(data?.events || []).map((r, i) => ({ ...r, id: `${r.organizerId}-${r.eventId}-${i}` }))}
            columns={eventColumns as unknown as GridColDef[]}
            getRowHeight={() => 'auto'}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            density="comfortable"
          />
        )}

        <div className={classes.SectionHeader}>
          <Typography variant="h6" color="warning.main">Payouts</Typography>
          <Button
            size="small"
            color="warning"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setPayoutWizardOpen(true)}
          >
            Add payout
          </Button>
        </div>

        {isFetching ? (
          <CircularProgress color="warning" />
        ) : (
          <DataGrid
            sx={{
              backgroundColor: '#ede9e4',
              borderRadius: '0px',
              border: 'none',
              marginBottom: '24px',
              '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
              '& .MuiDataGrid-main': { overflow: 'visible' },
              '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', position: 'sticky', zIndex: 2, borderRadius: '0px', border: '0px' },
              '.MuiDataGrid-columnHeaderTitleContainer': { color: '#fff', fontSize: '15px', padding: '10px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
              '.MuiDataGrid-cell': { border: '1px solid #fc7f03', padding: '5px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
              '.MuiDataGrid-footerContainer': { border: '1px solid #fc7f03' },
              '.MuiDataGrid-footerContainer p': { margin: '0px' },
              '.MuiDataGrid-columnHeaderTitle': { overflow: 'visible', lineHeight: '1.43rem', whiteSpace: 'normal' },
            }}
            rows={(payoutsForPerson || []).map((p) => ({ ...p }))}
            columns={payoutColumns as unknown as GridColDef[]}
            getRowHeight={() => 'auto'}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            density="comfortable"
          />
        )}

        <AddPayoutWizard
          open={payoutWizardOpen}
          onClose={() => setPayoutWizardOpen(false)}
          onSuccess={(id) => {
            setPayoutWizardOpen(false);
            refetch();
            refetchPayouts();
            navigate(`/marketing-persons/payouts/${id}`);
          }}
          marketingPersonId={marketingPersonId}
        />

        <AddAssignmentModal
          open={addAssignmentOpen}
          onClose={() => setAddAssignmentOpen(false)}
          onSuccess={() => { setAddAssignmentOpen(false); refetch(); }}
          marketingPersonId={marketingPersonId}
        />

        <OrganizerCommentsModal
          open={commentsRow !== null}
          onClose={() => setCommentsRow(null)}
          onChanged={refetch}
          assignmentId={commentsRow?.assignmentId ?? null}
          organizerName={commentsRow?.organizerName}
        />

        <Dialog open={unbindRow !== null} onClose={() => setUnbindRow(null)}>
          <DialogTitle color="warning.main">{translate('marketingPersonsUnbind')}: {unbindRow?.organizerName}</DialogTitle>
          <DialogContent>
            <Typography>{translate('marketingPersonsUnbindConfirm')}</Typography>
            {unbindError && <Typography color="error" mt={1}>{unbindError}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnbindRow(null)} color="warning">{translate('marketingPersonsCancel')}</Button>
            <Button onClick={handleUnbind} color="error" variant="contained" disabled={isUnbinding}>{translate('marketingPersonsUnbind')}</Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </div>
  );
};

export default MarketingPersonDetailsScreen;
